/**
 * A "how this works" dialog opened from a "?" button: a title, one tab per
 * topic and a "Got it" footer. The Business Opportunities scoring guide uses
 * it; another screen reuses it with its own tabs.
 */

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Modal } from './Modal';
import { nextIndex } from './Tabs';

export interface GuideTab {
  key: string;
  name: string;
  panel: ReactNode;
}

export function GuideDialog({
  title,
  subtitle,
  label,
  tabs,
  onClose,
}: {
  title: string;
  subtitle: string;
  /** Names the tablist for assistive technology. */
  label: string;
  tabs: GuideTab[];
  onClose: () => void;
}) {
  const [open, setOpen] = useState(tabs[0]?.key);
  const id = useId();
  const strip = useRef<HTMLDivElement>(null);
  const current = tabs.find((tab) => tab.key === open) ?? tabs[0];

  // Left and Right (wrapping), Home and End move to a tab and open it, as in ui/Tabs.
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = nextIndex(event.key, index, tabs.length);
    if (target === null) return;
    event.preventDefault();
    setOpen(tabs[target]?.key);
    strip.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus();
  }

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="wide"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Got it
        </button>
      }
    >
      <div ref={strip} className="tabs guide-tabs" role="tablist" aria-label={label}>
        {tabs.map(({ key, name }, index) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${id}-tab-${key}`}
            aria-selected={current?.key === key}
            aria-controls={`${id}-panel`}
            className={`tab${current?.key === key ? ' is-active' : ''}`}
            tabIndex={current?.key === key ? 0 : -1}
            onClick={() => setOpen(key)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {name}
          </button>
        ))}
      </div>

      {current && (
        <section className="guide-section" role="tabpanel" tabIndex={0} id={`${id}-panel`} aria-labelledby={`${id}-tab-${current.key}`}>
          {current.panel}
        </section>
      )}
    </Modal>
  );
}
